import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { TOKEN_KEY } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { CreditCard, Save, Eye, EyeOff, ExternalLink, RotateCcw, Mail, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin card for PayPal + Venmo credentials. Mirrors the EmailSettingsCard pattern:
 * secret is masked once saved, admin can re-type to overwrite. All values persist to
 * db.system_config._id="payments" via /api/admin/billing-settings.
 */
export default function BillingSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");
  const [secretSet, setSecretSet] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [mode, setMode] = useState("sandbox");
  const [enabled, setEnabled] = useState(false);
  const [priceUsd, setPriceUsd] = useState("19");
  const [venmoHandle, setVenmoHandle] = useState("");
  const [productName, setProductName] = useState("");
  const [webhookId, setWebhookId] = useState("");
  const [history, setHistory] = useState([]);
  const [refundingId, setRefundingId] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);

  const webhookEndpoint = typeof window !== "undefined"
    ? `${window.location.origin}/api/billing/webhook`
    : "/api/billing/webhook";

  const load = async () => {
    try {
      const { data } = await api.get("/admin/billing-settings");
      setClientId(data.client_id || "");
      setSecretSet(!!data.secret_set);
      setSecret("");
      setMode(data.mode || "sandbox");
      setEnabled(!!data.enabled);
      setPriceUsd(String(data.price_usd ?? 19));
      setVenmoHandle(data.venmo_handle || "");
      setProductName(data.product_name || "");
      setWebhookId(data.webhook_id || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/billing/history");
      setHistory(data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); loadHistory(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      client_id: clientId.trim(),
      mode,
      enabled,
      price_usd: parseFloat(priceUsd),
      venmo_handle: venmoHandle.trim(),
      product_name: productName.trim() || "Amino Exchange — Premium Access",
      webhook_id: webhookId.trim(),
    };
    if (secret && secret.trim()) payload.secret = secret.trim();
    try {
      await api.patch("/admin/billing-settings", payload);
      toast.success("Payment settings saved");
      setSecret("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const refund = async (p) => {
    if (!window.confirm(`Refund ${p.user_email || p.user_id} $${Number(p.amount_usd).toFixed(2)}? This will revoke their premium.`)) return;
    setRefundingId(p.id);
    try {
      await api.post(`/admin/billing/refund/${p.id}`);
      toast.success("Refund issued, premium revoked");
      await loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefundingId(null);
    }
  };

  const sendTestDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/digest/send-test");
      if (data.ok) toast.success(`Preview digest sent to ${data.email}`);
      else toast.error(data.reason || "Failed to send digest");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingDigest(false);
    }
  };

  const sendTestReminder = async () => {
    setSendingReminder(true);
    try {
      const { data } = await api.post("/admin/dose-reminder/send-test");
      if (data.ok) toast.success(`Preview reminder sent to ${data.email}`);
      else toast.error(data.reason || "Failed to send reminder");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingReminder(false);
    }
  };

  if (loading) return null;

  return (
    <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="admin-billing-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-[#0D9488] flex items-center justify-center">
          <CreditCard className="w-4 h-4 text-black" />
        </div>
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// Monetization</div>
          <div className="font-display font-black uppercase tracking-tight text-xl">Payments — PayPal + Venmo</div>
        </div>
      </div>

      <div className="mb-4 border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 leading-relaxed">
        <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">// Setup</div>
        <ol className="list-decimal ml-4 space-y-0.5">
          <li>Log into <a href="https://developer.paypal.com/dashboard/applications/sandbox" target="_blank" rel="noopener noreferrer" className="text-[#0D9488] hover:underline inline-flex items-center gap-0.5">developer.paypal.com <ExternalLink className="w-3 h-3" /></a> with your PayPal Business account.</li>
          <li>Create a REST API app → copy the <strong className="text-slate-900">Client ID</strong> and <strong className="text-slate-900">Secret</strong>.</li>
          <li>Paste them below. Start in <strong className="text-amber-400">Sandbox</strong> — flip to Live after you've verified a test purchase.</li>
          <li>Venmo shows up automatically inside the PayPal buttons for US-based mobile users — no extra step required.</li>
        </ol>
      </div>

      <form onSubmit={save} className="space-y-4">
        <div>
          <Label htmlFor="paypal-client-id" className="text-[11px] font-mono-num uppercase tracking-widest text-slate-600">PayPal Client ID</Label>
          <Input id="paypal-client-id" value={clientId} onChange={(e) => setClientId(e.target.value)}
            placeholder="AeA1QIZXiflr..."
            className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num text-sm mt-1"
            data-testid="paypal-client-id-input" />
        </div>

        <div>
          <Label htmlFor="paypal-secret" className="text-[11px] font-mono-num uppercase tracking-widest text-slate-600">
            PayPal Secret {secretSet && <span className="text-emerald-400 ml-2">✓ saved</span>}
          </Label>
          <div className="relative mt-1">
            <Input id="paypal-secret" type={showSecret ? "text" : "password"} value={secret} onChange={(e) => setSecret(e.target.value)}
              placeholder={secretSet ? "•••••••••••••••• (leave blank to keep)" : "EOJ7...paste secret here"}
              className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num text-sm pr-10"
              data-testid="paypal-secret-input" />
            <button type="button" onClick={() => setShowSecret((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900" data-testid="paypal-secret-toggle">
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-[11px] font-mono-num uppercase tracking-widest text-slate-600">Mode</Label>
            <div className="flex mt-1 border border-slate-200">
              {[
                { key: "sandbox", label: "Sandbox" },
                { key: "live", label: "Live" },
              ].map((m) => (
                <button key={m.key} type="button" onClick={() => setMode(m.key)}
                  className={`flex-1 h-10 text-xs font-mono-num uppercase tracking-widest transition-colors ${
                    mode === m.key ? (m.key === "live" ? "bg-red-500 text-black" : "bg-amber-500 text-black") : "bg-slate-50 text-slate-600 hover:text-slate-900"
                  }`} data-testid={`paypal-mode-${m.key}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="paypal-price" className="text-[11px] font-mono-num uppercase tracking-widest text-slate-600">Price (USD)</Label>
            <Input id="paypal-price" type="number" min="0.01" step="0.01" value={priceUsd} onChange={(e) => setPriceUsd(e.target.value)}
              className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num text-sm mt-1"
              data-testid="paypal-price-input" />
          </div>

          <div className="flex items-end">
            <label className="w-full flex items-center gap-2 h-10 border border-slate-200 bg-slate-50 px-3 cursor-pointer">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
                className="accent-[#0D9488]" data-testid="paypal-enabled-toggle" />
              <span className="text-[11px] uppercase tracking-widest font-mono-num text-slate-800">
                {enabled ? "Payments Enabled" : "Payments OFF"}
              </span>
            </label>
          </div>
        </div>

        <div>
          <Label htmlFor="paypal-product" className="text-[11px] font-mono-num uppercase tracking-widest text-slate-600">Product label (shown at checkout)</Label>
          <Input id="paypal-product" value={productName} onChange={(e) => setProductName(e.target.value)}
            placeholder="Amino Exchange — Premium Access"
            className="bg-slate-50 border-slate-200 rounded-none h-10 text-sm mt-1"
            data-testid="paypal-product-input" />
        </div>

        <div>
          <Label htmlFor="venmo-handle" className="text-[11px] font-mono-num uppercase tracking-widest text-slate-600">
            Venmo @handle <span className="text-slate-400 normal-case tracking-normal">(optional — shown as a P2P fallback)</span>
          </Label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-mono-num">@</span>
            <Input id="venmo-handle" value={venmoHandle} onChange={(e) => setVenmoHandle(e.target.value.replace(/^@+/, ""))}
              placeholder="your-venmo-handle"
              className="bg-slate-50 border-slate-200 rounded-none h-10 pl-8 text-sm"
              data-testid="venmo-handle-input" />
          </div>
          <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
            Shows on the checkout modal as an alternate. Users pay you directly on Venmo, then you flip their premium flag manually in
            <span className="text-slate-800"> Admin → Users</span>.
          </p>
        </div>

        <div className="border-t border-slate-200 pt-4">
          <Label htmlFor="paypal-webhook-id" className="text-[11px] font-mono-num uppercase tracking-widest text-slate-600">
            Webhook ID <span className="text-slate-400 normal-case tracking-normal">(from PayPal developer dashboard → Webhooks)</span>
          </Label>
          <Input id="paypal-webhook-id" value={webhookId} onChange={(e) => setWebhookId(e.target.value)}
            placeholder="WH-xxxxxxxxxxxxxxxx-xxxxxxxxxxxxxxxx"
            className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num text-sm mt-1"
            data-testid="paypal-webhook-id-input" />
          <div className="mt-2 border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600 leading-relaxed">
            <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">// Webhook URL to paste at PayPal</div>
            <code className="text-slate-900 font-mono-num break-all" data-testid="paypal-webhook-url">{webhookEndpoint}</code>
            <p className="mt-2">
              At PayPal, subscribe this URL to: <span className="text-slate-900">PAYMENT.CAPTURE.REFUNDED</span>, <span className="text-slate-900">PAYMENT.CAPTURE.REVERSED</span>, <span className="text-slate-900">PAYMENT.CAPTURE.DENIED</span>, and <span className="text-slate-900">CUSTOMER.DISPUTE.CREATED</span>. Copy the Webhook ID PayPal returns and paste it above so we can verify signatures.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end pt-2 border-t border-slate-200">
          <Button type="submit" disabled={saving}
            className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-10 px-6 uppercase tracking-widest text-xs font-semibold"
            data-testid="paypal-save-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save payment settings"}
          </Button>
        </div>
      </form>

      {history.length > 0 && (
        <div className="mt-6 border-t border-slate-200 pt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// Recent captures ({history.length})</div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/billing/export.csv`}
              onClick={(e) => {
                // Ensure the browser sends the bearer token via a fetch+blob download
                // (a plain <a> download won't include Authorization headers).
                e.preventDefault();
                const token = localStorage.getItem(TOKEN_KEY);
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/billing/export.csv`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
                  .then((r) => { if (!r.ok) throw new Error(`Export failed (${r.status})`); return r.blob(); })
                  .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `amino-payments-${new Date().toISOString().slice(0, 10)}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                    toast.success(`Exported ${history.length} payment${history.length === 1 ? "" : "s"}`);
                  })
                  .catch((err) => toast.error(err.message || "Export failed"));
              }}
              className="inline-flex items-center gap-1 border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-600 text-[10px] font-mono-num uppercase tracking-widest px-2 py-1 transition-colors"
              data-testid="payments-export-csv"
            >
              <Download className="w-3 h-3" /> Download CSV
            </a>
          </div>
          <div className="max-h-56 overflow-y-auto border border-slate-200" data-testid="paypal-history">
            {history.slice(0, 20).map((p) => {
              const refunded = p.status === "refunded" || p.status === "reversed";
              return (
                <div key={p.id} className="flex items-center justify-between px-3 py-2 border-b border-slate-200 text-xs gap-3">
                  <div className="flex-1 min-w-0">
                    <div className={`truncate ${refunded ? "text-slate-500 line-through" : "text-slate-900"}`}>{p.user_email || p.user_id}</div>
                    <div className="text-[10px] text-slate-500 font-mono-num truncate">
                      {p.order_id} · {p.mode}
                      {refunded && <span className="ml-2 text-red-400 uppercase not-italic">{p.status}</span>}
                    </div>
                  </div>
                  <div className={`font-mono-num tabular-nums ${refunded ? "text-slate-400 line-through" : "text-[#0D9488]"}`}>
                    ${Number(p.amount_usd || 0).toFixed(2)}
                  </div>
                  {!refunded && (
                    <button
                      type="button"
                      onClick={() => refund(p)}
                      disabled={refundingId === p.id}
                      className="inline-flex items-center gap-1 border border-slate-200 hover:border-red-500/60 hover:text-red-400 text-slate-600 text-[10px] font-mono-num uppercase tracking-widest px-2 py-1 transition-colors disabled:opacity-40 disabled:cursor-wait"
                      data-testid={`refund-btn-${p.id}`}
                    >
                      <RotateCcw className="w-3 h-3" /> {refundingId === p.id ? "…" : "Refund"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 border-t border-slate-200 pt-5">
        <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">// Weekly Digest</div>
        <div className="flex items-center justify-between gap-4 border border-slate-200 bg-slate-50 p-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-900">Preview the Sunday digest right now</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Sends a live copy to your own admin inbox using your past-7-day activity.</div>
          </div>
          <Button
            type="button"
            onClick={sendTestDigest}
            disabled={sendingDigest}
            className="bg-transparent border border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488] hover:text-black rounded-none h-9 px-4 uppercase tracking-widest text-[10px] font-mono-num transition-colors"
            data-testid="send-test-digest-btn"
          >
            <Mail className="w-3.5 h-3.5 mr-2" /> {sendingDigest ? "Sending…" : "Send test digest"}
          </Button>
        </div>

        <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 mt-5 mb-2">// Smart Dose Reminders</div>
        <div className="flex items-center justify-between gap-4 border border-slate-200 bg-slate-50 p-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-900">Preview the overdue-dose reminder</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Auto-runs every 15 min for users with notifications on. One reminder per overdue shot,
              nudges them to log the next dose + tag how they're feeling.
            </div>
          </div>
          <Button
            type="button"
            onClick={sendTestReminder}
            disabled={sendingReminder}
            className="bg-transparent border border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488] hover:text-black rounded-none h-9 px-4 uppercase tracking-widest text-[10px] font-mono-num transition-colors"
            data-testid="send-test-reminder-btn"
          >
            <Mail className="w-3.5 h-3.5 mr-2" /> {sendingReminder ? "Sending…" : "Send test reminder"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
